/* eslint-disable id-denylist */
import { HttpService } from 'http/httpService'
import { LoggingService, EventType, ActionEventData } from 'http/loggingService'
import { pathUtils } from 'utils/pathnameFormatter'
import { ErrorResponse } from '../../types'
import {
    DiscountCodeBEForm,
    DiscountCodeDetails,
    DiscountPool,
    DiscountPoolResponse,
    DiscountCodeResponse,
    DiscountCodeCursorPaginatedResponse,
    GetDiscountUploadDetailsResponse,
} from './types'

export class DiscountsService {
    constructor(
        private httpService: HttpService,
        private loggingService: LoggingService,
        private backofficeEndpoint: string,
    ) {}

    async getDiscountPools({
        accountSlug,
        query,
    }: {
        accountSlug: string
        query?: string
    }): Promise<DiscountPoolResponse> {
        const logEventType: EventType = 'get_discount_pools'
        const logEventData: ActionEventData = {
            category: 'discount_codes',
            payload: { accountSlug, query },
        }

        const response = await this.httpService.fetch(
            pathUtils.addQueryString(`${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_pools/`, query),
            { method: 'GET' },
        )

        if (!response.ok) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to get discount pools')
        }

        this.loggingService.logAction(logEventType, logEventData)

        return response.json()
    }

    async getDiscountCodes({
        accountSlug,
        query,
    }: {
        accountSlug: string
        query?: string
    }): Promise<DiscountCodeResponse> {
        const logEventType: EventType = 'get_discount_codes'
        const logEventData: ActionEventData = {
            category: 'discount_codes',
            payload: { accountSlug, query },
        }

        const response = await this.httpService.fetch(
            pathUtils.addQueryString(`${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_codes/`, query),
            { method: 'GET' },
        )

        if (!response.ok) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to get discount codes')
        }

        this.loggingService.logAction(logEventType, logEventData)

        return response.json()
    }

    async getDiscountCodesCursorPaginated({
        accountSlug,
        query,
    }: {
        accountSlug: string
        query?: string
    }): Promise<DiscountCodeCursorPaginatedResponse> {
        const logEventType: EventType = 'get_discount_codes'
        const logEventData: ActionEventData = {
            category: 'discount_codes',
            payload: { accountSlug, query },
        }

        const response = await this.httpService.fetch(
            pathUtils.addQueryString(`${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_codes/`, query),
            { method: 'GET' },
        )

        if (!response.ok) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to get discount codes')
        }

        this.loggingService.logAction(logEventType, logEventData)

        return response.json()
    }

    async addDiscountPool({ accountSlug, name }: { accountSlug: string; name: string }): Promise<DiscountPool> {
        const logEventType: EventType = 'add_discount_pool'
        const logEventData: ActionEventData = {
            category: 'discount_codes',
            payload: { accountSlug, name },
        }

        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_pools/`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({ name }),
            },
        )

        try {
            this.loggingService.logAction(logEventType, logEventData)

            return response.json()
        } catch (error) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to create discount code')
        }
    }

    async addCodeByForm({
        accountSlug,
        poolUuid,
        formData,
    }: {
        accountSlug: string
        poolUuid: string
        formData: DiscountCodeBEForm
    }): Promise<DiscountCodeDetails | ErrorResponse<DiscountCodeDetails>> {
        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_pools/${poolUuid}/discount_codes/`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(formData),
            },
        )

        return this.loggingService.handleResponse({
            response,
            logEventType: 'add_discount_code',
            logEventData: {
                category: 'discount_codes',
                payload: { accountSlug, poolUuid },
            },
        })
    }

    async deleteDiscountPool({ accountSlug, uuid }: { accountSlug: string; uuid: string }) {
        const logEventType: EventType = 'delete_discount_pool'
        const logEventData: ActionEventData = {
            category: 'discount_codes',
            payload: { accountSlug, uuid },
        }

        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_pools/${uuid}/`,
            {
                method: 'DELETE',
            },
        )

        if (!response.ok) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to delete discount pool')
        }

        this.loggingService.logAction(logEventType, logEventData)
    }

    async editDiscountPool({ accountSlug, uuid, name }: { accountSlug: string; uuid: string; name: string }) {
        const logEventType: EventType = 'edit_discount_pool'
        const logEventData: ActionEventData = {
            category: 'discount_codes',
            payload: { accountSlug, uuid },
        }

        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_pools/${uuid}/`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({ name }),
            },
        )

        if (!response.ok) {
            this.loggingService.logResponseError(response, logEventType, logEventData)
            throw new Error('Unable to edit discount pool')
        }

        this.loggingService.logAction(logEventType, logEventData)
    }

    async getDiscountCodeDetails({
        accountSlug,
        codeUuid,
        poolUuid,
    }: {
        accountSlug: string
        poolUuid: string
        codeUuid: string
    }) {
        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_pools/${poolUuid}/discount_codes/${codeUuid}/`,
            {
                method: 'GET',
            },
        )

        return this.loggingService.handleResponse<DiscountCodeDetails>({
            response,
            logEventType: 'get_discount_code_details',
            logEventData: {
                category: 'discount_codes',
                payload: {
                    accountSlug,
                    poolUuid,
                    codeUuid,
                },
            },
        })
    }

    async editDiscountCode({
        accountSlug,
        codeUuid,
        poolUuid,
        formData,
    }: {
        accountSlug: string
        poolUuid: string
        codeUuid: string
        formData: DiscountCodeBEForm
    }) {
        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_pools/${poolUuid}/discount_codes/${codeUuid}/`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(formData),
            },
        )

        return this.loggingService.handleResponse({
            response,
            logEventType: 'update_discount_code',
            logEventData: {
                category: 'discount_codes',
                payload: {
                    accountSlug,
                    poolUuid,
                    codeUuid,
                },
            },
        })
    }

    async redeemUnusedDiscountCodesFromUploads({ accountSlug, uuid }: { accountSlug: string; uuid: string }) {
        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_upload/${uuid}/redeem/`,
            {
                method: 'POST',
            },
        )

        return this.loggingService.handleResponse({
            response,
            logEventType: 'redeem_discount_codes',
            logEventData: {
                category: 'discount_codes',
                payload: { accountSlug, uuid },
            },
        })
    }

    async redeemUnusedDiscountCodesFromCode({ accountSlug, uuid }: { accountSlug: string; uuid: string }) {
        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_codes/${uuid}/redeem/`,
            {
                method: 'POST',
            },
        )

        return this.loggingService.handleResponse({
            response,
            logEventType: 'redeem_discount_codes',
            logEventData: {
                category: 'discount_codes',
                payload: { accountSlug, uuid },
            },
        })
    }

    async uploadDiscountCode({
        accountSlug,
        poolUuid,
        file,
    }: {
        accountSlug: string
        poolUuid: string
        file: File
    }): Promise<DiscountCodeDetails | ErrorResponse<DiscountCodeDetails>> {
        const formData = new FormData()
        formData.set('file', file)

        const response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_pools/${poolUuid}/upload/`,
            {
                method: 'POST',
                body: formData,
            },
        )

        return this.loggingService.handleResponse({
            logEventType: 'upload_discount_codes',
            logEventData: {
                category: 'discount_codes',
                payload: { accountSlug, poolUuid, file: file.name },
            },
            response,
        })
    }

    async getDiscountUploadDetails({
        accountSlug,
        id,
    }: {
        accountSlug: string
        id: string
    }): Promise<GetDiscountUploadDetailsResponse> {
        let response = await this.httpService.fetch(
            `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/discount_uploads/${id}/`,
            {
                method: 'GET',
            },
        )

        return this.loggingService.handleResponse({
            response,
            logEventType: 'get_discount_upload_details',
            logEventData: {
                category: 'discount_codes',
                payload: { accountSlug, id },
            },
        })
    }
}
